/**
 * Making an EXTERNAL agent's newly created node visible.
 *
 * `uiRefresh.js` fixed half of the owner's 2026-09-09 report: an MCP write
 * bumps `documentsUiStore.treeVersion`, so the sidebar re-reads. The other
 * half is that re-reading is not the same as *seeing*. A folder created inside
 * a collapsed folder, or a subpage created under a document that is not
 * expanded, lands correctly in the tree and stays behind a closed chevron —
 * which from the owner's side is indistinguishable from the bug we just fixed.
 *
 * The in-app path has always done this. `DocumentsSidebar.js:449-472`
 * (`handleNewFolder` / `handleNewDocument`) calls `expandFolder(parentId)`,
 * with a comment stating the principle: creating something the user cannot see
 * is not a create. MCP is the same asymmetry the refresh bug was — the
 * precedent exists, this module follows it rather than inventing one.
 *
 * ## What this does NOT do — the boundary is the same one uiRefresh.js draws
 *
 * The in-app path also calls `setSelected` / `setEditing` / `openDocumentTab`,
 * and the in-app AI path adds `navigateToPanel`. All of that is right for a
 * human who just clicked "+" and is looking at the result. It is wrong here:
 * an MCP caller is an external process — Claude Code in a terminal, an editor
 * plugin, a background agent — and the user may be doing something else
 * entirely. **Expand an ancestor in place; never navigate, never open a tab,
 * never select, never start an inline rename.** `mcpUiRefresh.test.js` pins
 * that no `src/mcp/` module mentions `navigateToPanel` / `openDocumentTab` /
 * `tabsStore` / `requestTaskFocus` in code, and that test covers this file.
 *
 * ## Only CREATES reveal
 *
 * `document_write` into an existing document, `document_rename`, and a
 * `memory_append` that appends to a note that already exists all change a node
 * the user already had. Yanking a folder open for them would move the tree
 * under a user who is reading it, for no new node. So the reveal set is
 * strictly the three tools that can bring a node into existence, and
 * `memory_append` only when it actually created its note (`created: true` from
 * `ensureNote`).
 *
 * A `memory_append` that creates the Agent Notes folder for the first time
 * DOES reveal, and that is not an exception: the note inside it is new, the
 * folder is at the section root so it needs no expansion of its own, and the
 * folder we expand was created microseconds earlier by the same call — it has
 * never had an expansion state, so this cannot be overriding a collapse the
 * user chose. (`memory_purge` deletes; there is nothing to reveal.)
 *
 * ## Why the chain is computed here and not in the sidebar
 *
 * A subpage under a document inside a folder needs BOTH the folder and the
 * document expanded, so the request carries the whole ancestor chain, not a
 * parent id. It is derived from the record store's own `listTree` for the
 * scope that was written — the same call `documentsTree` and `documentRename`
 * already make, so this adds no new import, no new `invoke`, and nothing to
 * `mcpAttackSurface.test.js`'s allow-list. The sidebar's expand set is
 * id-keyed and shared by folders and documents-with-children
 * (`DocumentsSidebar.js:809-812`), so one flat list of ids expands both kinds.
 */
import { listTree, ownDocumentScopes } from '../extensions/bundled/documents/documentsStore';
import { requestDocumentReveal } from '../extensions/bundled/documents/documentsUiStore';
import { toDocScope, PERSONAL } from './scopes';

/**
 * The tools that can bring a NEW node into existence. A strict subset of
 * `uiRefresh.js`'s `MUTATING_TOOLS`: every one of these bumps the tree, but
 * `document_write` / `document_rename` / `memory_purge` bump without revealing.
 */
export const CREATING_TOOLS = Object.freeze(
  new Set(['document_create_folder', 'document_create', 'memory_append'])
);

/**
 * Did this successful result actually create a node?
 *
 * `memory_append` is the only one that can go either way — `ensureNote`
 * returns `created: false` when the day's note already existed, which is the
 * common case (an agent appending to today's note all afternoon).
 */
function createdNode(tool, result) {
  if (!CREATING_TOOLS.has(tool)) return false;
  if (!result || typeof result !== 'object' || !result.id) return false;
  if (tool === 'memory_append') return result.created === true;
  return true;
}

/**
 * `nodeId`'s ancestors in `wire`, ROOT-FIRST, excluding the node itself.
 * `[]` for a node at the section root. Throws nothing to the caller's caller:
 * a store read that fails is the caller's problem to swallow.
 */
export async function ancestorChain(wire, nodeId) {
  const tree = await listTree(toDocScope(wire));
  return chainIn(new Map((tree || []).map((n) => [String(n.id), n])), nodeId);
}

/** `ancestorChain` over an already-read id → node map. */
function chainIn(byId, nodeId) {
  const chain = [];
  let cur = byId.get(String(nodeId));
  // Bounded by the map size: a cycle in the stored tree (the hazard
  // `useSidebarDrag`'s cycle guard exists to prevent) must not hang a write.
  const seen = new Set([String(nodeId)]);
  while (cur && cur.parentId != null) {
    const parentId = String(cur.parentId);
    if (seen.has(parentId)) break;
    seen.add(parentId);
    chain.unshift(parentId);
    cur = byId.get(parentId);
  }
  return chain;
}

/**
 * Called by `refreshUiAfterTool` on a tool's SUCCESS path, BEFORE the tree
 * nonce is bumped — the order matters and is pinned by a test. The bump is
 * what makes the sidebar re-read, and the sidebar consumes the reveal after
 * that read completes, so the request has to already be parked when the bump
 * lands. Setting it afterwards would be a race against an async re-read.
 *
 * Returns the reveal nonce, or `null` if nothing was requested.
 */
export async function requestRevealAfterTool(tool, result) {
  if (!createdNode(tool, result)) return null;
  const wire = result.scope || PERSONAL;
  // A tree read that fails means we do not KNOW the ancestor chain. Requesting
  // a reveal with an empty chain would be indistinguishable from a root-level
  // create and would silently do nothing; requesting nothing is the honest
  // outcome, and the write itself is unaffected — it already succeeded.
  const chain = await ancestorChain(wire, result.id);
  return requestDocumentReveal(wire, result.id, chain);
}

// ── document deep links (card 5.3) ───────────────────────────────────────────

const LINK_PREFIX = 'apiboo://documents/';
const DEVICE = 'device';
const LINK_SEGMENT = /^[A-Za-z0-9_-]{1,64}$/;
const LINK_ID = /^[A-Za-z0-9._:-]{1,200}$/;

function decodeSegment(raw) {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * A document link → `{ wire, scopeId, docId }`, or null when it is not one.
 * Three shapes:
 *
 *   apiboo://documents/<wire scope>/<docId>            (the MCP resource URI)
 *   apiboo://documents/personal/<scopeId>/<docId>      (a page in a workspace)
 *   apiboo://documents/personal%2F<scopeId>/<docId>    (the MCP URI of such a page)
 *
 * `<wire scope>` is `personal`, `team:<id>` (`team%3A<id>` as the generators
 * percent-encode it), or `device` — a page kept on this device (cloud/local
 * items), which is how docMarkdown writes links to such pages.
 *
 * The workspace segment is a HINT for the sidebar — which tree to show — not
 * an address: the document is found by id in the whole personal bucket
 * (`locateDocument`), so a link made before the page moved to another
 * workspace still opens it. `scopeId` follows the backend's shape rule
 * (`[A-Za-z0-9_-]{1,64}`); anything else is not a link. Mirrored in Rust by
 * `content_link::extract_document_link`, which reads the link the OS hands over.
 */
export function parseDocumentLink(uri) {
  const text = String(uri || '').trim();
  if (!text.startsWith(LINK_PREFIX)) return null;
  const parts = text.slice(LINK_PREFIX.length).split(/[?#]/)[0].replace(/\/+$/, '').split('/').map(decodeSegment);
  if (parts.some((p) => p == null || p === '')) return null;
  let wire;
  let scopeId = null;
  let docId;
  if (parts.length === 2) [wire, docId] = parts;
  else if (parts.length === 3 && parts[0] === PERSONAL && LINK_SEGMENT.test(parts[1])) [wire, scopeId, docId] = parts;
  else return null;
  if (parts.length === 2 && wire.startsWith(`${PERSONAL}/`)) {
    scopeId = wire.slice(PERSONAL.length + 1);
    if (!LINK_SEGMENT.test(scopeId)) return null;
    wire = PERSONAL;
  }
  if (wire !== PERSONAL && wire !== DEVICE && !/^team:[A-Za-z0-9_-]{1,64}$/.test(wire)) return null;
  if (!LINK_ID.test(docId)) return null;
  return { wire, scopeId, docId };
}

/**
 * Where a parsed link's page actually is now: `{ sectionId, ancestorIds,
 * node, scope }` for the sidebar's reveal and the tab — `sectionId` is the
 * switcher key of the tree holding it (`personal`, `personal/<scopeId>`,
 * `device`, `team:<id>`), read from the tree rather than the link, so a page
 * moved since the link was copied is still revealed where it is. A personal
 * or device link looks in BOTH of the user's own scopes (a page can move
 * between the cloud and this device keeping its id — `ownDocumentScopes`);
 * a team link only in that team. Null when the page is not there.
 */
export async function documentLinkTarget(link) {
  if (!link) return null;
  const candidates = link.wire === PERSONAL || link.wire === DEVICE
    ? ownDocumentScopes(link.wire === DEVICE ? { type: DEVICE } : { type: PERSONAL })
    : [toDocScope(link.wire)];
  for (const scope of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const tree = await listTree(scope);
    const byId = new Map((tree || []).map((n) => [String(n.id), n]));
    const node = byId.get(String(link.docId));
    if (!node) continue;
    const chain = chainIn(byId, link.docId);
    if (scope.type === DEVICE) return { sectionId: DEVICE, ancestorIds: chain, node, scope: { type: DEVICE } };
    if (scope.type === 'team') return { sectionId: `team:${scope.teamId}`, ancestorIds: chain, node, scope };
    const sectionId = node.scopeId ? `${PERSONAL}/${node.scopeId}` : PERSONAL;
    return { sectionId, ancestorIds: chain, node, scope: { type: PERSONAL, ...(node.scopeId ? { scopeId: node.scopeId } : {}) } };
  }
  return null;
}
